/**
 * hybridStorage.js
 * Enhanced utility for managing data storage with IndexedDB, Google Drive, and localStorage fallbacks
 */

import {
  saveTransactionsToDrive,
  loadTransactionsFromDrive,
  saveDepreciationToDrive,
  loadDepreciationFromDrive,
  saveServicePricesToDrive,
  loadServicePricesFromDrive,
  saveCostItemsToDrive,
  loadCostItemsFromDrive,
  saveExtraCategoriesToDrive,
  loadExtraCategoriesFromDrive,
  saveGoalsToDrive,
  loadGoalsFromDrive,
  saveVatSettingsToDrive,
  loadVatSettingsFromDrive,
  saveClientsToDrive,
  loadClientsFromDrive,
  saveBusinessProfileToDrive,
  loadBusinessProfileFromDrive,
  isSignedIn,
} from './googleDriveService';

import {
  transactionStorage,
  servicePricesStorage,
  depreciationStorage,
  settingsStorage,
  dbUtils,
  connectionMonitor,
  SYNC_STATUS
} from './services/offlineStorageService';
import { mergeClientLists } from './lib/clientUtils';
  
  // LocalStorage keys
  const KEYS = {
    TRANSACTIONS: '70livesbeautyledgerTransactions',
    DEPRECIATION: '70livesbeautyledgerDepreciation',
    SERVICE_PRICES: '70livesbeautyledgerServicePrices',
    COST_ITEMS: '70lives_cost_items',
    EXTRA_CATEGORIES: 'extra_categories',
    GOALS: '70livesbeautyledgerGoals',
    VAT_SETTINGS: 'vat_settings',
    CLIENTS: '70livesbeautyledgerClients',
    BUSINESS_PROFILE: '70livesbeautyledgerBusinessProfile',
    BANK_ACCOUNTS: '70livesbeautyledgerBankAccounts',
    JOURNAL_ENTRIES: '70livesbeautyledgerJournalEntries',
  };

  const TRANSIENT_CACHE_KEYS = [
    KEYS.TRANSACTIONS,
    KEYS.DEPRECIATION,
    KEYS.SERVICE_PRICES,
    KEYS.COST_ITEMS,
    KEYS.EXTRA_CATEGORIES,
    KEYS.GOALS,
    KEYS.VAT_SETTINGS,
    KEYS.CLIENTS,
    KEYS.BUSINESS_PROFILE,
    KEYS.BANK_ACCOUNTS,
    KEYS.JOURNAL_ENTRIES,
    '70livesbeautyledgerTargetIncome',
    'transactions_backup',
    'service_prices_backup',
    'cost_items_backup',
    'clients_backup',
    'business_profile_backup',
    'goals_backup',
    'vat_settings_backup',
    'extra_categories_backup',
    'bank_accounts_backup',
    'journal_entries_backup'
  ];

  const isEmptyData = (data) => {
    if (data === null || data === undefined) return true;
    if (Array.isArray(data)) return data.length === 0;
    if (typeof data === 'object') return Object.keys(data).length === 0;
    return false;
  };

  const hasData = (data) => !isEmptyData(data);

  export const normalizeBankAccounts = (bankAccounts) => {
    const now = new Date().toISOString();
    return (Array.isArray(bankAccounts) ? bankAccounts : [])
      .filter((account) => account && typeof account === 'object')
      .map((account, index) => {
        const balance = Number(account.balance);
        return {
          id: account.id || `bank-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
          name: String(account.name || '').trim() || `Bank Account ${index + 1}`,
          accountType: String(account.accountType || 'current').trim() || 'current',
          accountNumberHint: String(account.accountNumberHint || '').trim(),
          balance: Number.isFinite(balance) ? balance : 0,
          asOfDate: account.asOfDate || new Date().toISOString().slice(0, 10),
          notes: String(account.notes || '').trim(),
          createdAt: account.createdAt || now,
          updatedAt: account.updatedAt || now
        };
      });
  };

  export const normalizeJournalEntries = (journalEntries) => {
    const now = new Date().toISOString();
    return (Array.isArray(journalEntries) ? journalEntries : [])
      .filter((entry) => entry && typeof entry === 'object')
      .map((entry, entryIndex) => ({
        id: entry.id || `journal-${Date.now()}-${entryIndex}-${Math.random().toString(36).slice(2, 7)}`,
        date: entry.date || new Date().toISOString().slice(0, 10),
        reference: String(entry.reference || '').trim() || `JNL-${String(entryIndex + 1).padStart(3, '0')}`,
        description: String(entry.description || '').trim(),
        lines: (Array.isArray(entry.lines) ? entry.lines : [])
          .filter((line) => line && typeof line === 'object')
          .map((line, lineIndex) => {
            const debit = Number(line.debit);
            const credit = Number(line.credit);
            return {
              id: line.id || `line-${entryIndex}-${lineIndex}-${Math.random().toString(36).slice(2, 7)}`,
              accountCode: String(line.accountCode || '').trim(),
              accountName: String(line.accountName || '').trim(),
              accountType: String(line.accountType || 'asset').trim() || 'asset',
              debit: Number.isFinite(debit) ? debit : 0,
              credit: Number.isFinite(credit) ? credit : 0,
              memo: String(line.memo || '').trim()
            };
          }),
        createdAt: entry.createdAt || now,
        updatedAt: entry.updatedAt || now
      }));
  };

  const getTransactionId = (transaction) => {
    if (!transaction || typeof transaction !== 'object') return null;
    const { id } = transaction;
    if (id === null || id === undefined || id === '') return null;
    return String(id);
  };

  const normalizeTransactionTextKey = (value) => (
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
  );

  const normalizeTransactionAmountKey = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : '';
  };

  const getIdlessTransactionKey = (transaction) => {
    if (!transaction || typeof transaction !== 'object') return null;

    const receiptId = normalizeTransactionTextKey(transaction.receiptId || transaction.sourceReceiptId || '');
    const receiptImageUrl = normalizeTransactionTextKey(transaction.receiptImageUrl || transaction.imageUrl || transaction.receiptUrl || '');
    const amount = normalizeTransactionAmountKey(transaction.amount);
    const date = normalizeTransactionTextKey(transaction.date || transaction.invoiceDate || transaction.paymentDate || '');
    const type = normalizeTransactionTextKey(transaction.type || '');
    const category = normalizeTransactionTextKey(transaction.category || '');
    const description = normalizeTransactionTextKey(transaction.description || transaction.vendor || transaction.supplierName || transaction.clientName || '');

    if (receiptId && amount) {
      return `receipt:${receiptId}|${amount}`;
    }

    if (receiptImageUrl && amount && date) {
      return `receipt-image:${receiptImageUrl}|${date}|${amount}`;
    }

    const createdAt = normalizeTransactionTextKey(transaction.createdAt || transaction.timestamp || '');
    const paymentMethod = normalizeTransactionTextKey(transaction.paymentMethod || '');
    const invoiceNumber = normalizeTransactionTextKey(transaction.invoiceNumber || transaction.referenceNumber || '');
    const fingerprint = [type, date, amount, category, description, paymentMethod, invoiceNumber, createdAt].join('|');

    return fingerprint.replace(/\|/g, '') ? `fingerprint:${fingerprint}` : null;
  };

  const parseTimestampValue = (value) => {
    if (value === null || value === undefined || value === '') return 0;

    if (value instanceof Date) {
      const ms = value.getTime();
      return Number.isNaN(ms) ? 0 : ms;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const ms = new Date(value).getTime();
      return Number.isNaN(ms) ? 0 : ms;
    }

    if (typeof value === 'object') {
      if (typeof value.toDate === 'function') {
        const dateValue = value.toDate();
        if (dateValue instanceof Date) {
          const ms = dateValue.getTime();
          if (!Number.isNaN(ms)) return ms;
        }
      }

      const seconds = Number(value.seconds ?? value._seconds);
      const nanoseconds = Number(value.nanoseconds ?? value._nanoseconds);
      if (Number.isFinite(seconds)) {
        return seconds * 1000 + (Number.isFinite(nanoseconds) ? Math.floor(nanoseconds / 1000000) : 0);
      }
    }

    const raw = String(value).trim();
    if (/^\d{10,13}$/.test(raw)) {
      const numeric = Number(raw);
      if (Number.isFinite(numeric)) {
        return raw.length === 10 ? numeric * 1000 : numeric;
      }
    }

    const ms = new Date(raw).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };

  const getTransactionTimestamp = (transaction) => {
    if (!transaction || typeof transaction !== 'object') return 0;
    const raw = transaction.updatedAt || transaction.createdAt || transaction.date;
    if (!raw) return 0;
    return parseTimestampValue(raw);
  };

  const sortTransactions = (transactions) => (
    [...transactions].sort((a, b) => getTransactionTimestamp(b) - getTransactionTimestamp(a))
  );

  const cacheTransactionsLocally = async (transactions) => {
    const normalizedTransactions = Array.isArray(transactions) ? transactions : [];
    const now = new Date().toISOString();

    try {
      localStorage.setItem(KEYS.TRANSACTIONS, JSON.stringify(normalizedTransactions));
      localStorage.setItem('transactions_backup', JSON.stringify(normalizedTransactions));
    } catch (backupError) {
      console.warn('Failed to refresh local transaction backup:', backupError);
    }

    try {
      const offlineDb = (await import('./services/offlineStorageService')).default;
      await offlineDb.transaction('rw', offlineDb.transactions, async () => {
        await offlineDb.transactions.clear();
        if (normalizedTransactions.length > 0) {
          await offlineDb.transactions.bulkPut(
            normalizedTransactions.map((transaction) => ({
              ...transaction,
              syncStatus: transaction.syncStatus || SYNC_STATUS.SYNCED,
              createdAt: transaction.createdAt || now,
              updatedAt: transaction.updatedAt || now
            }))
          );
        }
      });
    } catch (indexedDBError) {
      console.warn('Failed to refresh IndexedDB transaction cache:', indexedDBError);
    }
  };

  const mergeTransactions = (driveTransactions, localTransactions) => {
    const mergedById = new Map();
    const mergedIdlessByKey = new Map();
    const idless = [];

    const addTransaction = (transaction) => {
      const id = getTransactionId(transaction);
      if (!id) {
        const idlessKey = getIdlessTransactionKey(transaction);
        if (idlessKey) {
          const existing = mergedIdlessByKey.get(idlessKey);
          if (!existing || getTransactionTimestamp(transaction) >= getTransactionTimestamp(existing)) {
            mergedIdlessByKey.set(idlessKey, transaction);
          }
          return;
        }

        idless.push(transaction);
        return;
      }

      const existing = mergedById.get(id);
      if (!existing) {
        mergedById.set(id, transaction);
        return;
      }

      const existingTimestamp = getTransactionTimestamp(existing);
      const incomingTimestamp = getTransactionTimestamp(transaction);
      if (incomingTimestamp >= existingTimestamp) {
        mergedById.set(id, transaction);
      }
    };

    (Array.isArray(driveTransactions) ? driveTransactions : []).forEach(addTransaction);
    (Array.isArray(localTransactions) ? localTransactions : []).forEach(addTransaction);

    return sortTransactions([...mergedById.values(), ...mergedIdlessByKey.values(), ...idless]);
  };

  const getCostItemKey = (item) => {
    if (!item || typeof item !== 'object') return null;
    const nameKey = (item.name || '').trim().toLowerCase();
    if (nameKey) return `name:${nameKey}`;
    if (item.id !== null && item.id !== undefined && item.id !== '') return `id:${String(item.id)}`;
    return null;
  };

  const getCostItemTimestamp = (item) => {
    if (!item || typeof item !== 'object') return 0;
    const raw = item.updatedAt || item.createdAt;
    if (!raw) return 0;
    return parseTimestampValue(raw);
  };

  const mergeCostItems = (driveItems, localItems) => {
    const merged = new Map();
    const idless = [];

    const addItem = (item) => {
      const key = getCostItemKey(item);
      if (!key) {
        idless.push(item);
        return;
      }

      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, item);
        return;
      }

      const existingTimestamp = getCostItemTimestamp(existing);
      const incomingTimestamp = getCostItemTimestamp(item);
      if (incomingTimestamp >= existingTimestamp) {
        merged.set(key, item);
      }
    };

    (Array.isArray(driveItems) ? driveItems : []).forEach(addItem);
    (Array.isArray(localItems) ? localItems : []).forEach(addItem);

    return [...merged.values(), ...idless];
  };

  const DEFAULT_EXTRA_CATEGORIES = { income: [], expenses: [] };

  const normalizeExtraCategories = (value) => {
    const source = value && typeof value === 'object' ? value : {};
    const normalizeList = (list) => {
      if (!Array.isArray(list)) return [];
      const seen = new Set();
      const result = [];

      list.forEach((entry) => {
        if (!entry || typeof entry !== 'object') return;

        const id = String(entry.id || '').trim();
        const name = String(entry.name || '').trim();
        const icon = String(entry.icon || '').trim();
        if (!id && !name) return;

        const normalizedId = id || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
        if (!normalizedId) return;
        const dedupeKey = normalizedId.toLowerCase();
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);

        result.push({
          id: normalizedId,
          name: name || normalizedId,
          icon
        });
      });

      return result;
    };

    return {
      income: normalizeList(source.income),
      expenses: normalizeList(source.expenses)
    };
  };

  const mergeExtraCategoryLists = (driveList, localList) => {
    const merged = new Map();

    const addCategory = (entry) => {
      if (!entry || typeof entry !== 'object') return;
      const id = String(entry.id || '').trim();
      const name = String(entry.name || '').trim();
      const icon = String(entry.icon || '').trim();
      if (!id && !name) return;

      const key = (id || name).toLowerCase();
      const existing = merged.get(key);
      if (!existing) {
        merged.set(key, {
          id: id || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''),
          name: name || id,
          icon
        });
        return;
      }

      merged.set(key, {
        id: id || existing.id,
        name: name || existing.name,
        icon: icon || existing.icon
      });
    };

    // Add Drive first, then local so unsynced local additions win for duplicate keys.
    (Array.isArray(driveList) ? driveList : []).forEach(addCategory);
    (Array.isArray(localList) ? localList : []).forEach(addCategory);

    return [...merged.values()];
  };

  const mergeExtraCategories = (driveCategories, localCategories) => {
    const normalizedDrive = normalizeExtraCategories(driveCategories);
    const normalizedLocal = normalizeExtraCategories(localCategories);

    return {
      income: mergeExtraCategoryLists(normalizedDrive.income, normalizedLocal.income),
      expenses: mergeExtraCategoryLists(normalizedDrive.expenses, normalizedLocal.expenses)
    };
  };

  const DEFAULT_GOALS = {
    amount: '',
    period: 'weekly',
    workDaysPerWeek: 5,
    workHoursPerWeek: 30,
    goalHourly: null
  };

  const normalizeGoals = (value) => {
    const source = value && typeof value === 'object' ? value : {};
    return {
      amount: source.amount ?? '',
      period: source.period || 'weekly',
      workDaysPerWeek: source.workDaysPerWeek ?? 5,
      workHoursPerWeek: source.workHoursPerWeek ?? 30,
      goalHourly: source.goalHourly ?? null,
      updatedAt: source.updatedAt || null
    };
  };

  const normalizeVatSettings = (value) => (
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}
  );

  const normalizeBusinessProfile = (value) => {
    const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    const directorDetails = source.directorDetails && typeof source.directorDetails === 'object' && !Array.isArray(source.directorDetails)
      ? source.directorDetails
      : {};
    return {
      businessType: source.businessType || '',
      industryId: source.industryId || '',
      businessName: source.businessName || '',
      tradingName: source.tradingName || '',
      businessAddress: source.businessAddress || '',
      companyRegistrationNumber: source.companyRegistrationNumber || '',
      incorporationDate: source.incorporationDate || '',
      taxReference: source.taxReference || '',
      directorDetails: {
        name: directorDetails.name || '',
        address: directorDetails.address || '',
        role: directorDetails.role || '',
        appointedDate: directorDetails.appointedDate || ''
      },
      updatedAt: source.updatedAt || null
    };
  };

  const normalizeClients = (value) => (
    Array.isArray(value) ? mergeClientLists([], value) : []
  );

  const mergeByLatestTimestamp = (driveValue, localValue, normalizeFunction) => {
    const normalizedDrive = normalizeFunction(driveValue);
    const normalizedLocal = normalizeFunction(localValue);
    const driveTimestamp = parseTimestampValue(normalizedDrive?.updatedAt);
    const localTimestamp = parseTimestampValue(normalizedLocal?.updatedAt);

    if (localTimestamp > driveTimestamp) return normalizedLocal;
    if (driveTimestamp > localTimestamp) return normalizedDrive;

    // Tie breaker: keep local to preserve unsynced local edits when timestamps match/missing.
    return hasData(normalizedLocal) ? normalizedLocal : normalizedDrive;
  };

  const loadLocalData = async (key, indexedDBLoadFunction) => {
    if (indexedDBLoadFunction) {
      try {
        const indexedDBData = await indexedDBLoadFunction();
        if (hasData(indexedDBData)) {
          console.log(`Data loaded from IndexedDB for key: ${key}`);
          return indexedDBData;
        }
      } catch (indexedDBError) {
        console.error(`Error loading from IndexedDB for key: ${key}:`, indexedDBError);
      }
    }

    try {
      const localData = localStorage.getItem(key);
      if (localData) {
        const parsedData = JSON.parse(localData);
        if (hasData(parsedData)) {
          console.log(`Data loaded from localStorage for key: ${key}`);
          return parsedData;
        }
      }
    } catch (localError) {
      console.error(`Error loading from localStorage for key: ${key}:`, localError);
    }

    return null;
  };
  
  /**
   * Enhanced save data to IndexedDB, localStorage, and Google Drive
   * @param {string} key - Storage key
   * @param {Function} driveSaveFunction - Google Drive save function
   * @param {Function} indexedDBSaveFunction - IndexedDB save function
   * @param {Array|Object} data - Data to save
   * @returns {Promise<boolean>} - Success status
   */
  const saveData = async (key, driveSaveFunction, indexedDBSaveFunction, data) => {
    let saveSuccessful = false;
    
    // 1. Save to IndexedDB first (primary offline storage)
    if (indexedDBSaveFunction) {
      try {
        await indexedDBSaveFunction(data);
        console.log(`Data saved to IndexedDB for key: ${key}`);
        saveSuccessful = true;
      } catch (indexedDBError) {
        console.error(`Error saving to IndexedDB (${key}):`, indexedDBError);
      }
    }
    
    // 2. Save to localStorage as backup
    try {
      localStorage.setItem(key, JSON.stringify(data));
      console.log(`Data saved to localStorage with key: ${key}`);
      saveSuccessful = true;
    } catch (localError) {
      console.error(`Error saving to localStorage (${key}):`, localError);
    }
    
    // 3. Save to Google Drive if online and signed in
    if (connectionMonitor.isOnline && isSignedIn()) {
      try {
        await driveSaveFunction(data);
        console.log(`Data saved to Google Drive for key: ${key}`);
        saveSuccessful = true;
      } catch (driveError) {
        console.error(`Error saving to Google Drive (${key}):`, driveError);
        // Data is still saved locally, so we're good
      }
    } else {
      console.log(`Offline or not signed into Drive, using local storage only for: ${key}`);
    }
    
    return saveSuccessful;
  };
  
  /**
   * Enhanced load data from IndexedDB, Google Drive, and localStorage fallback
   * @param {string} key - Storage key
   * @param {Function} driveLoadFunction - Google Drive load function
   * @param {Function} indexedDBLoadFunction - IndexedDB load function
   * @returns {Promise<Array|Object>} - Loaded data
   */
  const loadData = async (key, driveLoadFunction, indexedDBLoadFunction) => {
    const isTransactionsKey = key === KEYS.TRANSACTIONS || key.includes('TRANSACTIONS');
    const isCostItemsKey = key === KEYS.COST_ITEMS;
    const isExtraCategoriesKey = key === KEYS.EXTRA_CATEGORIES;
    const isGoalsKey = key === KEYS.GOALS;
    const isVatSettingsKey = key === KEYS.VAT_SETTINGS;
    const isClientsKey = key === KEYS.CLIENTS;
    const isBusinessProfileKey = key === KEYS.BUSINESS_PROFILE;
    const isBankAccountsKey = key === KEYS.BANK_ACCOUNTS;
    const isJournalEntriesKey = key === KEYS.JOURNAL_ENTRIES;
    const localData = await loadLocalData(key, indexedDBLoadFunction);

    // 1. Try Google Drive first if online and signed in (most up-to-date)
    if (connectionMonitor.isOnline && isSignedIn()) {
      try {
        console.log(`Attempting to load data from Drive for key: ${key}`);
        const driveData = await driveLoadFunction();

        if (driveData !== undefined && driveData !== null) {
          const driveHasData = hasData(driveData);
          const localHasData = hasData(localData);

          if (!driveHasData && localHasData) {
            console.warn(`Drive returned empty data for ${key}. Preserving local data instead.`);
            if (isExtraCategoriesKey) return normalizeExtraCategories(localData);
            if (isGoalsKey) return normalizeGoals(localData);
            if (isVatSettingsKey) return normalizeVatSettings(localData);
            if (isBusinessProfileKey) return normalizeBusinessProfile(localData);
            if (isBankAccountsKey) return normalizeBankAccounts(localData);
            if (isJournalEntriesKey) return normalizeJournalEntries(localData);
            return localData;
          }

          if (isTransactionsKey && driveHasData && localHasData) {
            const mergedTransactions = mergeTransactions(driveData, localData);
            await cacheTransactionsLocally(mergedTransactions);
            return mergedTransactions;
          }

          if (isCostItemsKey && driveHasData && localHasData) {
            const mergedItems = mergeCostItems(driveData, localData);
            try {
              localStorage.setItem(key, JSON.stringify(mergedItems));
            } catch (backupError) {
              console.warn(`Failed to backup merged data to localStorage for key: ${key}`, backupError);
            }
            return mergedItems;
          }

          if (isExtraCategoriesKey && driveHasData && localHasData) {
            const mergedCategories = mergeExtraCategories(driveData, localData);
            try {
              localStorage.setItem(key, JSON.stringify(mergedCategories));
            } catch (backupError) {
              console.warn(`Failed to backup merged data to localStorage for key: ${key}`, backupError);
            }
            return mergedCategories;
          }

          if (isGoalsKey && driveHasData && localHasData) {
            const mergedGoals = mergeByLatestTimestamp(driveData, localData, normalizeGoals);
            try {
              localStorage.setItem(key, JSON.stringify(mergedGoals));
            } catch (backupError) {
              console.warn(`Failed to backup merged goals to localStorage for key: ${key}`, backupError);
            }
            return mergedGoals;
          }

          if (isVatSettingsKey && driveHasData && localHasData) {
            const mergedVatSettings = mergeByLatestTimestamp(driveData, localData, normalizeVatSettings);
            try {
              localStorage.setItem(key, JSON.stringify(mergedVatSettings));
            } catch (backupError) {
              console.warn(`Failed to backup merged VAT settings to localStorage for key: ${key}`, backupError);
            }
            return mergedVatSettings;
          }

          if (isBusinessProfileKey && driveHasData && localHasData) {
            const mergedBusinessProfile = mergeByLatestTimestamp(driveData, localData, normalizeBusinessProfile);
            try {
              localStorage.setItem(key, JSON.stringify(mergedBusinessProfile));
            } catch (backupError) {
              console.warn(`Failed to backup merged business profile to localStorage for key: ${key}`, backupError);
            }
            return mergedBusinessProfile;
          }

          if (isClientsKey && driveHasData && localHasData) {
            const mergedClients = mergeClientLists(localData, driveData);
            try {
              localStorage.setItem(key, JSON.stringify(mergedClients));
            } catch (backupError) {
              console.warn(`Failed to backup merged client data to localStorage for key: ${key}`, backupError);
            }
            return mergedClients;
          }

          if (!isTransactionsKey && driveHasData && localHasData && typeof driveData === 'object' && !Array.isArray(driveData) && typeof localData === 'object' && !Array.isArray(localData)) {
            const mergedObject = { ...localData, ...driveData };
            try {
              localStorage.setItem(key, JSON.stringify(mergedObject));
            } catch (backupError) {
              console.warn(`Failed to backup merged data to localStorage for key: ${key}`, backupError);
            }
            return mergedObject;
          }

          console.log(`Data loaded from Drive for key: ${key}`);
          const normalizedDriveData = (() => {
            if (isExtraCategoriesKey) return normalizeExtraCategories(driveData);
            if (isGoalsKey) return normalizeGoals(driveData);
            if (isVatSettingsKey) return normalizeVatSettings(driveData);
            if (isBusinessProfileKey) return normalizeBusinessProfile(driveData);
            if (isClientsKey) return normalizeClients(driveData);
            if (isBankAccountsKey) return normalizeBankAccounts(driveData);
            if (isJournalEntriesKey) return normalizeJournalEntries(driveData);
            return driveData;
          })();
          try {
            localStorage.setItem(key, JSON.stringify(normalizedDriveData));
          } catch (backupError) {
            console.warn(`Failed to backup Drive data to localStorage for key: ${key}`, backupError);
          }
          if (isTransactionsKey) {
            await cacheTransactionsLocally(normalizedDriveData);
          }

          return normalizedDriveData;
        }
      } catch (driveError) {
        console.error(`Error loading from Drive for key: ${key}:`, driveError);
        // Fall back to local sources below
      }
    }

    if (hasData(localData)) {
      if (isExtraCategoriesKey) return normalizeExtraCategories(localData);
      if (isGoalsKey) return normalizeGoals(localData);
      if (isVatSettingsKey) return normalizeVatSettings(localData);
      if (isBusinessProfileKey) return normalizeBusinessProfile(localData);
      if (isClientsKey) return normalizeClients(localData);
      if (isBankAccountsKey) return normalizeBankAccounts(localData);
      if (isJournalEntriesKey) return normalizeJournalEntries(localData);
      return localData;
    }

    // Default empty data
    if (key.includes('TRANSACTIONS') || key === KEYS.COST_ITEMS) {
      return [];
    }
    if (isExtraCategoriesKey) {
      return DEFAULT_EXTRA_CATEGORIES;
    }
    if (isGoalsKey) {
      return DEFAULT_GOALS;
    }
    if (isVatSettingsKey) {
      return {};
    }
    if (isBusinessProfileKey) {
      return {};
    }
    if (isClientsKey) {
      return [];
    }
    if (isBankAccountsKey || isJournalEntriesKey) {
      return [];
    }
    return {};
  };
  
  // Enhanced exported functions for specific data types
  export const saveTransactions = async (transactions) => {
    // For transactions, we need to handle individual transaction saves differently
    // This is for bulk saves (like initial load or sync)
    return saveData(
      KEYS.TRANSACTIONS, 
      saveTransactionsToDrive, 
      async (data) => {
        // Clear and bulk add to IndexedDB
        const db = (await import('./services/offlineStorageService')).default;
        await db.transactions.clear();
        const transactionsWithMeta = data.map(t => ({
          ...t,
          syncStatus: SYNC_STATUS.SYNCED,
          createdAt: t.createdAt || new Date().toISOString(),
          updatedAt: t.updatedAt || new Date().toISOString()
        }));
        await db.transactions.bulkAdd(transactionsWithMeta);
      },
      transactions
    );
  };
  
  export const loadTransactions = async () => {
    return loadData(
      KEYS.TRANSACTIONS, 
      loadTransactionsFromDrive,
      () => transactionStorage.getAll()
    );
  };
  
  export const saveDepreciation = async (depreciation) => {
    return saveData(
      KEYS.DEPRECIATION, 
      saveDepreciationToDrive, 
      () => depreciationStorage.save(depreciation),
      depreciation
    );
  };
  
  export const loadDepreciation = async () => {
    return loadData(
      KEYS.DEPRECIATION, 
      loadDepreciationFromDrive,
      () => depreciationStorage.load()
    );
  };
  
  export const saveServicePrices = async (servicePrices) => {
    return saveData(
      KEYS.SERVICE_PRICES, 
      saveServicePricesToDrive, 
      () => servicePricesStorage.save(servicePrices),
      servicePrices
    );
  };
  
  export const loadServicePrices = async () => {
    return loadData(
      KEYS.SERVICE_PRICES, 
      loadServicePricesFromDrive,
      () => servicePricesStorage.load()
    );
  };

  export const saveCostItems = async (costItems) => {
    const now = new Date().toISOString();
    const normalizedItems = Array.isArray(costItems)
      ? costItems.map((item) => ({
          ...item,
          createdAt: item?.createdAt || now,
          updatedAt: now
        }))
      : [];

    return saveData(
      KEYS.COST_ITEMS,
      saveCostItemsToDrive,
      () => settingsStorage.set('costItems', normalizedItems),
      normalizedItems
    );
  };

  export const loadCostItems = async () => {
    return loadData(
      KEYS.COST_ITEMS,
      loadCostItemsFromDrive,
      () => settingsStorage.get('costItems', [])
    );
  };

  export const saveExtraCategories = async (extraCategories) => {
    const normalizedCategories = normalizeExtraCategories(extraCategories);

    return saveData(
      KEYS.EXTRA_CATEGORIES,
      saveExtraCategoriesToDrive,
      () => settingsStorage.set('extraCategories', normalizedCategories),
      normalizedCategories
    );
  };

  export const loadExtraCategories = async () => {
    return loadData(
      KEYS.EXTRA_CATEGORIES,
      loadExtraCategoriesFromDrive,
      () => settingsStorage.get('extraCategories', DEFAULT_EXTRA_CATEGORIES)
    );
  };

  // Enhanced goal data functions with cloud + IndexedDB support
  export const saveGoals = async (goals) => {
    const normalizedGoals = {
      ...normalizeGoals(goals),
      updatedAt: new Date().toISOString()
    };

    return saveData(
      KEYS.GOALS,
      saveGoalsToDrive,
      () => settingsStorage.set('goals', normalizedGoals),
      normalizedGoals
    );
  };

  export const loadGoals = async () => {
    return loadData(
      KEYS.GOALS,
      loadGoalsFromDrive,
      () => settingsStorage.get('goals', DEFAULT_GOALS)
    );
  };

  export const saveVatSettings = async (vatSettings) => {
    const normalizedVatSettings = {
      ...normalizeVatSettings(vatSettings),
      updatedAt: new Date().toISOString()
    };

    return saveData(
      KEYS.VAT_SETTINGS,
      saveVatSettingsToDrive,
      () => settingsStorage.set('vatSettings', normalizedVatSettings),
      normalizedVatSettings
    );
  };

  export const loadVatSettings = async () => {
    return loadData(
      KEYS.VAT_SETTINGS,
      loadVatSettingsFromDrive,
      () => settingsStorage.get('vatSettings', {})
    );
  };

  export const saveBusinessProfile = async (businessProfile) => {
    const normalizedBusinessProfile = {
      ...normalizeBusinessProfile(businessProfile),
      updatedAt: new Date().toISOString()
    };

    return saveData(
      KEYS.BUSINESS_PROFILE,
      saveBusinessProfileToDrive,
      () => settingsStorage.set('businessProfile', normalizedBusinessProfile),
      normalizedBusinessProfile
    );
  };

  export const loadBusinessProfile = async () => {
    return loadData(
      KEYS.BUSINESS_PROFILE,
      loadBusinessProfileFromDrive,
      () => settingsStorage.get('businessProfile', {})
    );
  };

  export const saveClients = async (clients) => {
    const normalizedClients = normalizeClients(clients);

    return saveData(
      KEYS.CLIENTS,
      saveClientsToDrive,
      () => settingsStorage.set('clients', normalizedClients),
      normalizedClients
    );
  };

  export const loadClients = async () => {
    return loadData(
      KEYS.CLIENTS,
      loadClientsFromDrive,
      () => settingsStorage.get('clients', [])
    );
  };

  export const saveBankAccounts = async (bankAccounts) => {
    const normalizedBankAccounts = normalizeBankAccounts(bankAccounts);

    return saveData(
      KEYS.BANK_ACCOUNTS,
      async () => true,
      () => settingsStorage.set('bankAccounts', normalizedBankAccounts),
      normalizedBankAccounts
    );
  };

  export const loadBankAccounts = async () => {
    return loadData(
      KEYS.BANK_ACCOUNTS,
      async () => null,
      () => settingsStorage.get('bankAccounts', [])
    );
  };

  export const saveJournalEntries = async (journalEntries) => {
    const normalizedJournalEntries = normalizeJournalEntries(journalEntries);

    return saveData(
      KEYS.JOURNAL_ENTRIES,
      async () => true,
      () => settingsStorage.set('journalEntries', normalizedJournalEntries),
      normalizedJournalEntries
    );
  };

  export const loadJournalEntries = async () => {
    return loadData(
      KEYS.JOURNAL_ENTRIES,
      async () => null,
      () => settingsStorage.get('journalEntries', [])
    );
  };

  // New functions for individual transaction management
  export const addTransaction = async (transaction) => {
    try {
      const savedTransaction = await transactionStorage.add(transaction);
      console.log('Transaction added to IndexedDB with sync queue');
      return savedTransaction;
    } catch (error) {
      console.error('Error adding transaction:', error);
      throw error;
    }
  };

  export const updateTransaction = async (id, updates) => {
    try {
      const updatedTransaction = await transactionStorage.update(id, updates);
      console.log('Transaction updated in IndexedDB with sync queue');
      return updatedTransaction;
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw error;
    }
  };

  export const deleteTransaction = async (id) => {
    try {
      await transactionStorage.delete(id);
      console.log('Transaction deleted from IndexedDB with sync queue');
    } catch (error) {
      console.error('Error deleting transaction:', error);
      throw error;
    }
  };

  // Sync status and utilities
  export const getSyncStatus = async () => {
    try {
      const { dbUtils } = await import('./services/offlineStorageService');
      const storageInfo = await dbUtils.getStorageInfo();
      const pendingTransactions = await transactionStorage.getPendingSync();
      
      return {
        ...storageInfo,
        pendingSync: pendingTransactions.length,
        isOnline: connectionMonitor.isOnline,
        isDriveConnected: isSignedIn()
      };
    } catch (error) {
      console.error('Error getting sync status:', error);
      return null;
    }
  };

  export const forceSyncNow = async () => {
    try {
      if (!connectionMonitor.isOnline) {
        throw new Error('Cannot sync while offline');
      }

      if (!isSignedIn()) {
        throw new Error('Google Drive permission is not connected');
      }

      const localTransactions = await loadLocalData(
        KEYS.TRANSACTIONS,
        () => transactionStorage.getAll()
      );

      let syncedTransactions = [];

      if (hasData(localTransactions)) {
        const driveTransactions = await loadTransactionsFromDrive().catch((error) => {
          console.warn('Could not load Drive transactions before manual sync; saving local data only:', error);
          return [];
        });
        const mergedTransactions = mergeTransactions(driveTransactions, localTransactions);
        await saveTransactionsToDrive(mergedTransactions);
        await cacheTransactionsLocally(mergedTransactions);
        syncedTransactions = mergedTransactions;
      }

      await connectionMonitor.triggerSync();
      console.log('Manual sync triggered');
      return {
        transactions: syncedTransactions,
        transactionCount: syncedTransactions.length
      };
    } catch (error) {
      console.error('Error during manual sync:', error);
      throw error;
    }
  };

  export const clearBrowserLedgerCache = async () => {
    try {
      if (typeof window !== 'undefined') {
        TRANSIENT_CACHE_KEYS.forEach((key) => {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            console.warn(`Failed to clear local cache key ${key}:`, error);
          }
        });
      }

      try {
        await dbUtils.clearAllData();
      } catch (dbError) {
        console.warn('Failed to clear IndexedDB ledger cache:', dbError);
      }
    } catch (error) {
      console.error('Failed to clear browser ledger cache:', error);
      throw error;
    }
  };
